import { Box, Divider, Stack, useMediaQuery, useTheme } from '@mui/material';
import React from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useHistory, useLocation } from 'react-router-dom';
import { ICourse } from '../api/CourseApi';
import { DialogApi } from '../api/DialogApi';
import { DialogPrompt, DialogPromptApi } from '../api/DialogPromptApi';
import { LessonApi } from '../api/LessonApi';
import { UserDialogPromptApi, UserDialogPromptUserSummary } from '../api/UserDialogPromptApi';
import AppBarContext from '../context/AppBarContext';
import DialogPromptFooterContext from '../context/DialogPromptFooterContext';
import { SentenceScoreDialogProvider } from '../context/SentenceScoreDialogContext';
import { DialogPromptComponentFactory } from './DialogView';
import { handleErrorMessage } from './ErrorDialog';

interface SettingsViewProps {
    course: ICourse;
    updateUserDialogPromptSummary: (summaryList: UserDialogPromptUserSummary[]) => void;
}

const SettingsView: React.FC<SettingsViewProps> = ({ course, updateUserDialogPromptSummary, }) => {

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();

    const { actionList, clearFooterList } = React.useContext(DialogPromptFooterContext)

    const [dialogPromptList, setDialogPromptList] = React.useState<DialogPrompt[]>([]);

    const chatBubbleContainerRef = React.useRef<HTMLDivElement>(null);
    const scrollContainerRef = React.useRef<HTMLDivElement>(null);
    const lastScrollTopRef = React.useRef(0);
    const lastUpScrollLogAtRef = React.useRef(0);

    React.useEffect(() => {
        LessonApi.getAll(course.id)
            .then(response => {
                if (response.status < 300) {
                    return response.data;
                }
                throw new Error("Unable to LessonApi.getAll() " + response.status)
            })
            .then(list => {
                const visibleList = list.filter(lesson => lesson.is_hidden === false);
                DialogApi.getAll(visibleList[0].id)
                    .then(response => {
                        if (response.status < 300) {
                            return response.data;
                        }
                        throw new Error("Unable to DialogApi.getAll() " + response.status)
                    })
                    .then(list => {
                        const visibleList = list.filter(dialog => dialog.is_hidden === false);
                        DialogPromptApi.getAll(visibleList[0].id)
                            .then(response => {
                                if (response.status < 300) {
                                    return response.data;
                                }
                                throw new Error("Unable to DialogPromptApi.getAll() " + response.status)

                            })
                            .then(list => {
                                const visibleList = list.filter(dialogPrompt => dialogPrompt.is_hidden === false);
                                setDialogPromptList(visibleList)
                                setCurrentDialogPromptIndex(1)
                            })
                            .catch(err => {
                                handleErrorMessage(err, setErrorMessage, history, location);
                            })
                    })
                    .catch(err => {
                        handleErrorMessage(err, setErrorMessage, history, location);
                    })
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
    }, [course.id, history, location, setErrorMessage]);

    const [currentDialogPromptIndex, setCurrentDialogPromptIndex] = React.useState(1);
    const activeDialogPromptId = dialogPromptList[currentDialogPromptIndex - 1]?.id ?? null;

    React.useEffect(() => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.dataset.agentAutoScrollLock = '0';
        }
    }, []);

    React.useEffect(() => {
        if (chatBubbleContainerRef.current) {
            const isLocked = scrollContainerRef.current?.dataset.agentAutoScrollLock === '1';
            if (isLocked) {
                return;
            }
            chatBubbleContainerRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [currentDialogPromptIndex]);

    React.useLayoutEffect(() => {
        clearFooterList();
    }, [activeDialogPromptId, clearFooterList]);

    const handleSettingsScroll = React.useCallback(() => {
        const scroller = scrollContainerRef.current;
        if (!scroller) return;
        const prevTop = lastScrollTopRef.current;
        const currentTop = scroller.scrollTop;
        const movingUp = currentTop < prevTop;
        const movingDown = currentTop > prevTop;
        const atBottom = scroller.scrollHeight - (currentTop + scroller.clientHeight) < 2;
        const now = Date.now();
        if (movingUp && (now - lastUpScrollLogAtRef.current > 200)) {
            lastUpScrollLogAtRef.current = now;
            if (scroller.dataset.agentAutoScrollLock !== '1') {
                scroller.dataset.agentAutoScrollLock = '1';
            }
        }
        if (movingDown && atBottom && scroller.dataset.agentAutoScrollLock === '1') {
            scroller.dataset.agentAutoScrollLock = '0';
        }
        lastScrollTopRef.current = currentTop;
    }, []);

    const setDialogPromptComplete = (dialogPromptId: number) => {

        // check the dialogPromptId, old dialogs can be clicked on.
        const currentDialogPrompt = dialogPromptList[currentDialogPromptIndex - 1];
        if (currentDialogPrompt && currentDialogPrompt.id == dialogPromptId) {
            clearFooterList();

            // dialogPrormptId == -1 if "lets continue" prompt, so don't complete it
            if (dialogPromptId >= 0) {
                UserDialogPromptApi.completeDialogPrompt(dialogPromptId)
                    .then(response => {
                        if (response.status < 300) {
                            return response.data;
                        }
                        throw new Error("Unable to UserDialogPromptApi.completeDialogPrompt " + response.status)

                    })
                    .then(summary => {
                        updateUserDialogPromptSummary(summary)
                    })
                    .catch(err => {
                        handleErrorMessage(err, setErrorMessage, history, location);
                    })

            }

            // currentDialogPromptIndex should always be right ... but search by ID anyway
            const slicedDialogPromptList = dialogPromptList.slice(currentDialogPromptIndex - 1);

            // Use findIndex() on the sliced array to find the next index of the dialogPromptId
            const indexOfTargetId = slicedDialogPromptList.findIndex((dialogPrompt) => dialogPrompt.id === dialogPromptId);

            // If the indexOfTargetId is not -1 (i.e., the targetId is found), adjust the index based on the currentDialogPromptIndex
            const finalIndex = indexOfTargetId !== -1 ? indexOfTargetId + currentDialogPromptIndex - 1 : -1;
            if (finalIndex >= 0) {
                // +1 because 1 based, +1 to advance to next dialog prompt
                setCurrentDialogPromptIndex(finalIndex + 2);
            } else {
                // if not found, just goto next index
                setCurrentDialogPromptIndex((prevIndex) => (prevIndex + 1));
            }

            // // also update index of original dialog prompt
            // const indexOfId = originalDialogPromptIdList.indexOf(dialogPromptId);
            // // +1 because 1 based, +1 to advance to next dialog prompt
            // setCurrentOriginalDialogPromptIdIndex(indexOfId + 2);
        }

    }

    const selectTeacherIndex = dialogPromptList.findIndex(
        (dialogPrompt) => dialogPrompt.class_name == "DialogPromptSelectTeacherModel"
    );

    // icon size
    // "small"     20px    for compact UI
    // "medium"    24px    standard
    // "large"     36px    prominent icons
    const theme = useTheme();
    const isCompact = useMediaQuery(theme.breakpoints.down("sm"));

    const actionButtonSize = isCompact ? '24px' : '36px'

    return (
        <DndProvider backend={HTML5Backend}>
            <Stack data-agent-settings-view="true" sx={{ margin: '0 auto', width: '95%', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <SentenceScoreDialogProvider>
                    <div data-agent-settings-scroll-container="true" ref={scrollContainerRef} onScroll={handleSettingsScroll} style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                        {dialogPromptList.slice(0, currentDialogPromptIndex).map((dialogPrompt, index) => {
                            const showAvatar = index > selectTeacherIndex;
                            return DialogPromptComponentFactory(
                                dialogPrompt,
                                setDialogPromptComplete,
                                chatBubbleContainerRef,
                                showAvatar,
                                true,
                                index === currentDialogPromptIndex - 1,
                            )
                        })}
                        <div ref={chatBubbleContainerRef} />
                    </div>
                </SentenceScoreDialogProvider>
                {/* FOOTER */}
                <Stack direction="column" alignItems="center" justifyContent="center" sx={{ flexShrink: 0 }}>
                    <Divider sx={{ width: '90%', borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }} />
                    {/* ACTION buttons */}
                    <Stack direction="row" alignItems="center" justifyContent="center" flexWrap="wrap" rowGap={1} sx={{ flexGrow: 1, my: 1, width: '100%', minHeight: 60 }} gap={1}>
                        {actionList.map((action, index) => (
                            <Box key={index} sx={{ minHeight: actionButtonSize, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{action}</Box>
                        ))}
                    </Stack>
                </Stack>
            </Stack >
        </DndProvider>
    );
};

export default SettingsView;
